import { appendClarificationChoice, ClarificationChoices, QueryMessage } from "./QueryMessage";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, FormControlLabel, FormGroup, Grid, IconButton, MenuItem, Paper, Radio, RadioGroup, Select, SelectChangeEvent, Switch, TextField, ThemeProvider, Typography } from "@mui/material";
import { xSifterQueryBuilderTheme } from "../../XSifterAppConstants";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import XSifterProcessInfoDialog from "./XSifterProcessInfoDialog";
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import "./XSifterQueryBuilder.css"
import { IDiscoverTextArchive, IDiscoverTextProject, IXSifterCreateSavedQueryResponse, IXSifterEstimateResponse, IXSifterQuery, IXSifterQueryRoot, IXSifterQueryRule, IXSifterSavedQuery } from "../../types/XSifterQueries";
import QueryBuilderComponent, { fieldsWithValueOptions } from "./QueryBuilderComponent";
import XSifterQueryService from "../../services/XSifterQueryService";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { showApiError } from "../../services/ApiErrorHelper";
import { clearDisplayMessage, setDisplayMessage, setDisplayMessageType, setHasQueryTitle, setCurrentQueryId } from "../../store/XSifterAppReducer";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useLocation, useNavigate, useSearchParams } from "react-router";
import dayjs, { Dayjs } from "dayjs";
import dayjsUtc from "dayjs/plugin/utc";
import { checkStripNoRetweets, isValidXQuery, translateQueryToXApi } from "../common/QueryFunctions";
import { IXSifterUser } from "../../types/XSifterAppUser";
import { formatDate, formatNumberString } from "../../types/FormattingHelper";
import LooksOneIcon from '@mui/icons-material/LooksOne';
import LooksTwoIcon from '@mui/icons-material/LooksTwo';
import Looks3Icon from '@mui/icons-material/Looks3';
import { AxiosError } from "axios";
import { IResponseBase, tryGetErrorMessage } from "../../types/XSifterGeneralTypes";

dayjs.extend(dayjsUtc);

// true once at least one filter row in the query tree is actually usable: a checkbox/select-style
// field (has/is/lang/retweetsOf) is complete as soon as it exists since its operator always has a
// default selection, but every other field type needs real text typed into its value.
const queryHasFilledFilter = (query: IXSifterQuery | IXSifterQueryRoot | null | undefined): boolean => {
    if (!query || !query.rules) {
        return false;
    }
    for (const node of query.rules) {
        const rule = node as IXSifterQueryRule;
        if (rule.field !== undefined) {
            if (fieldsWithValueOptions.includes(rule.field)) {
                if (rule.operator) {
                    return true;
                }
            } else if (rule.value !== undefined && rule.value !== null && String(rule.value).trim().length > 0) {
                return true;
            }
        } else if (queryHasFilledFilter(node as IXSifterQuery)) {
            return true;
        }
    }
    return false;
}

interface IXSifterQueryBuilderProps {
    allowEdits: boolean,
    queryTitle: string,
    currentUser: IXSifterUser | undefined,
    savedQuery?: IXSifterSavedQuery | null,
    savedQueryId?: string | null,
    duplicateQuery?: boolean | null,
    isInRevision?: boolean | null,
    onForceHeaderRemount: ()=>void
}

// the "to" date can be at most this far before "now"
const MIN_TO_DATE_BUFFER_MS = 60 * 1000;
const getMaxToDate = (): Date => new Date(Date.now() - MIN_TO_DATE_BUFFER_MS);

const XSifterQueryBuilder: React.FC<IXSifterQueryBuilderProps> = (props: IXSifterQueryBuilderProps) => {
    const [initialQueryNeedsClarification, setInitialQueryNeedsClarification] = useState<string | null>(null);
    const [clarificationChoices, setClarificationChoices] = useState<string[]>([]);
    const [fromDate, setFromDate] = useState<string | null>()
    const [toDate, setToDate] = useState<string | null>(getMaxToDate().toISOString());
    const [showCustomDateRange, setShowCustomDateRange] = useState<boolean>(false);
    const [dateRangeSetting, setDateRangeSetting] = useState<number>(3);
    const [queryTitle, setQueryTitle] = useState<string>("");
    const [userQuery, setUserQuery] = useState<IXSifterQueryRoot | null>(null);
    const [xApiQuery, setXApiQuery] = useState<IXSifterQuery | undefined | null>();
    const [isGettingEstimate, setIsGettingEstimate] = useState<boolean>(true);
    const [estimateResponse, setEstimateResponse] = useState<IXSifterEstimateResponse | undefined>();
    const [lastEstimateCount, setLastEstimateCount] = useState<number>(0);
    const [projectList, setProjectList] = useState<IDiscoverTextProject[] | null>(null);
    const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
    const [openEstimateDialog, setOpenEstimateDialog] = useState<boolean>(false);
    const [openEstimateInfoDialog, setOpenEstimateInfoDialog] = useState<boolean>(false);
    const [openProcessInfoDialog, setOpenProcessInfoDialog] = useState<boolean>(false);
    const [estimateTooLarge, setEstimateTooLarge] = useState<boolean>(false);
    const [estimateErrorMessage, setEstimateErrorMessage] = useState<string>("");
    const [openProjectDialog, setOpenProjectDialog] = useState<boolean>(false);
    const [openSubmitConfirmDialog, setOpenSubmitConfirmDialog] = useState<boolean>(false);
    const [openArchiveNameDialog, setOpenArchiveNameDialog] = useState<boolean>(false);
    const [newArchiveName, setNewArchiveName] = useState<string>("");
    const [archiveList, setArchiveList] = useState<IDiscoverTextArchive[] | null>(null);
    const [isLoadingArchiveList, setIsLoadingArchiveList] = useState<boolean>(false);
    const [archiveDestination, setArchiveDestination] = useState<"new" | "existing">("new");
    const [selectedExistingArchiveId, setSelectedExistingArchiveId] = useState<number | null>(null);
    const [createUserDescArchive, setCreateUserDescArchive] = useState<boolean>(false);
    // null = not edited by the user; the name defaults to the selected archive name + suffix
    const [userDescArchiveNameOverride, setUserDescArchiveNameOverride] = useState<string | null>(null);
    const [userDescDestination, setUserDescDestination] = useState<"new" | "existing">("new");
    const [selectedUserDescArchiveId, setSelectedUserDescArchiveId] = useState<number | null>(null);
    const [includeRetweets, setIncludeRetweets] = useState<boolean>(false);
    const [currentSavedQueryId, setCurrentSavedQueryId] = useState<string | null>(props.savedQueryId ?? null);
    const [queryInitialized, setQueryInitialized] = useState<boolean>(false);

    const [useBuilder, setUseBuilder] = useState<boolean>(false);
    const [queryDescription, setQueryDescription] = useState<string>("");
    const [queryRationale, setQueryRationale] = useState<string | null>(null);
    const [queryRationaleEdited, setQueryRationaleEdited] = useState<boolean>(false);
    // serialized builder query when the rationale was shown; null until the builder reports its initial query
    const rationaleBaselineQuery = useRef<string | null>(null);
    const queryTraceIdInitialized = useRef<boolean>(false);
    const [isSubmittingDescription, setIsSubmittingDescription] = useState<boolean>(false);
    const [searchParams] = useSearchParams();
    const [showChoice, setShowChoice] = useState<boolean>(searchParams.get("new") === "true" && !useBuilder);

    const queryService = new XSifterQueryService();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();

    queryService.setQueryTraceId(useAppSelector((state) => state.app.currentQueryId));

    useEffect(() => {
        // reget the current user
        props.onForceHeaderRemount();
        if (props.queryTitle && props.queryTitle.length > 0) {
            onSetQueryTitle(props.queryTitle);
        }
        // a brand new (or duplicated) query starts a new trace, so don't carry over the ID left in the
        // store from a previous query - but only once per mount, so loading a duplicated query's saved
        // data (which re-runs this effect) doesn't swap the ID out from under the user mid-session.
        // Reopening an existing saved query (e.g. coming back from the refine page) keeps the current ID.
        const startsNewQuery = !queryTraceIdInitialized.current && (!props.savedQueryId || !!props.duplicateQuery);
        queryTraceIdInitialized.current = true;
        if (startsNewQuery || queryService.getQueryTraceId() === null) {
            // create new query ID
            queryService.setQueryTraceId(queryService.generateNewQueryId());
            dispatch(setCurrentQueryId(queryService.getQueryTraceId()));
        }
            
        if (props.savedQuery) {
            const [queryFromDate, queryToDate, rawQuery] = parseSavedRawQuery(JSON.stringify(props.savedQuery.rawQuery));
            setUserQuery(rawQuery);
            setFromDate(queryFromDate);
            setToDate(queryToDate);
            setDateRangeSetting(8);
            setShowCustomDateRange(true);
            setQueryInitialized(true);

            // a refinement was just applied - get a fresh estimate for the refined query
            if ((location.state as { refined?: boolean } | null)?.refined) {
                // clear the flag so a page reload doesn't re-run the estimate
                // (done directly on history so the router doesn't re-render/remount and drop the estimate state)
                window.history.replaceState({ ...window.history.state, usr: null }, "");
                const refinedQuery: IXSifterQueryRoot = JSON.parse(JSON.parse(JSON.stringify(props.savedQuery.rawQuery)));
                setIsGettingEstimate(true);
                queryService.getEstimate(refinedQuery)
                .then((response) => {
                    queryService.setQueryTraceId(response.currentQueryId);
                    dispatch(setCurrentQueryId(queryService.getQueryTraceId()));
                    setEstimateResponse(response);
                    setLastEstimateCount(response.count);
                }).catch((error) => {
                    dispatch(setDisplayMessage(typeof error === "string" ? error : JSON.stringify(error)));
                    dispatch(setDisplayMessageType("error"))
                }).finally(() => {
                    setIsGettingEstimate(false);
                });
            }
        } else {
            let now = getMaxToDate()
            setToDate(now.toISOString());
            now.setHours(now.getHours() - 24);
            setFromDate(now.toISOString());
            setShowCustomDateRange(false);
            setDateRangeSetting(3);
            if (!props.savedQueryId && !props.duplicateQuery) {
                // no saved query to wait on - safe to mount the builder with a fresh query
                setQueryInitialized(true);
            }
        }
    }, [props.savedQuery, props.savedQueryId, props.duplicateQuery]);

    const normalizeQueryForBuilder = (query: IXSifterQuery): IXSifterQuery => {
        const isRuleNode = (node: IXSifterQuery | IXSifterQueryRule): node is IXSifterQueryRule => {
            return (node as IXSifterQueryRule).field !== undefined;
        }

        return {
            ...query,
            combinator: typeof query.combinator === "string" ? query.combinator.toLowerCase() : "and",
            isNot: !!query.isNot,
            rules: query.rules?.map(node => {
                if (isRuleNode(node)) {
                    return {
                        ...node,
                        combinator: typeof node.combinator === "string" ? node.combinator.toLowerCase() : "and",
                        isNot: !!node.isNot,
                    };
                }
                return normalizeQueryForBuilder(node);
            }) ?? null,
        };
    }

    const parseSavedRawQuery = (query: string): any => {
        let asObject = JSON.parse(JSON.parse(query));
        let {fromDate, ...queryWithoutFromDate} = asObject;
        let {toDate, ...queryWithoutDates} = queryWithoutFromDate;

        let finalQuery = checkStripNoRetweets(queryWithoutDates);
        let retQuery = normalizeQueryForBuilder(finalQuery);

        return [fromDate, toDate, retQuery];
    }

    const onQueryDescriptionSubmit = () => {
        if (!queryDescription || queryDescription.length === 0)
            return;

        setIsSubmittingDescription(true);
        queryService.getInitialQueryFromDescription(queryDescription)
            .then((response) => {
                queryService.setQueryTraceId(response.currentQueryId);
                dispatch(setCurrentQueryId(queryService.getQueryTraceId()));
                if (response.needsClarification) {
                    setInitialQueryNeedsClarification(response.needsClarification);
                    setClarificationChoices(response.clarificationChoices ?? []);
                    return;
                }

                if (!response.results || response.results.length === 0) {
                    // no results??
                    dispatch(setDisplayMessage('Could not translate the query from the description. Edit your description and try again.'));
                    dispatch(setDisplayMessageType("error"))
                    return;
                }

                // call to translate Twitter query
                return queryService.translateTwitterQuery(response.results[0].query)
                    .then((translated) => {
                        let query = normalizeQueryForBuilder(translated as IXSifterQuery);
                        let rationale = response.results[0].rationale;
                        rationale = "I've created this query because it " + rationale[0].toLowerCase() + rationale.slice(1);
                        if (response.results[0].queryTitle)
                            onSetQueryTitle(response.results[0].queryTitle);
                        setQueryRationale(rationale);
                        setQueryRationaleEdited(false);
                        rationaleBaselineQuery.current = null;
                        setUserQuery(query);
                        setShowChoice(false);
                        setUseBuilder(true);
                    }).catch((error) => {
                        dispatch(setDisplayMessage('Could not translate the query from the description. Edit your description and try again.'));
                        dispatch(setDisplayMessageType("error"))
                    })
            }).catch((error: IResponseBase) => {
                dispatch(setDisplayMessage(error.error?.errorMessage ?? "An unknown error occurred."));
                dispatch(setDisplayMessageType("error"))
            }).finally(() => setIsSubmittingDescription(false));
    }

    const onUpdateRequest = (choice: string | null) => {
        if (choice)
            setQueryDescription((current) => appendClarificationChoice(current, choice));
        setInitialQueryNeedsClarification(null);
        setClarificationChoices([]);
    }

    const onQueryTitleChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(clearDisplayMessage());
        setQueryRationaleEdited(true);
        onSetQueryTitle(event.target.value);
    }

    const onSetQueryTitle = (title: string) => {
        setQueryTitle(title);
        setNewArchiveName(title);
        setIsGettingEstimate(title?.length === 0);
    }

    const handleQueryChange = useCallback((query: IXSifterQueryRoot) => {
        dispatch(clearDisplayMessage());
        let translatedQuery = translateQueryToXApi(query, includeRetweets, fromDate, toDate);
        setXApiQuery(translatedQuery);
        setUserQuery(query);
        if (queryRationale) {
            // the builder reports its initial query on mount; only later differences are user edits
            const serialized = JSON.stringify(query);
            if (rationaleBaselineQuery.current === null) {
                rationaleBaselineQuery.current = serialized;
            } else if (rationaleBaselineQuery.current !== serialized) {
                setQueryRationaleEdited(true);
            }
        }
    }, [dispatch, includeRetweets, fromDate, toDate, queryRationale]);

    const onIncludeRetweetsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIncludeRetweets(event.target.checked);
        setXApiQuery(translateQueryToXApi(userQuery, event.target.checked,fromDate, toDate));
    }

    const handleFromDateChange = (value: Dayjs | null) => {
        // the picker operates in UTC (see the "timezone" prop below), so its Dayjs value already
        // represents the intended UTC instant - convert it straight to an ISO string for storage.
        const isoValue = value ? value.toISOString() : null;

        if (toDate && isoValue) {
            let toDateParsed = Date.parse(toDate);
            let fromDateParsed = Date.parse(isoValue);
            if (toDateParsed <= fromDateParsed) {
                dispatch(setDisplayMessage('The "to" date must be after the "from" date.'));
                dispatch(setDisplayMessageType("error"))
                return;
            }
        }
        if (isoValue) {
            let fromDateParsed = Date.parse(isoValue);
            if (fromDateParsed > Date.now()) {
                dispatch(setDisplayMessage('The "from" date cannot be after the current date and time.'));
                dispatch(setDisplayMessageType("error"))
                return;
            }
        }

        setXApiQuery(translateQueryToXApi(userQuery, includeRetweets, isoValue, toDate));
        setFromDate(isoValue);
        dispatch(clearDisplayMessage());
    }

    const handleToDateChange = (value: Dayjs | null) => {
        // the picker operates in UTC (see the "timezone" prop below), so its Dayjs value already
        // represents the intended UTC instant - convert it straight to an ISO string for storage.
        const isoValue = value ? value.toISOString() : null;

        if (fromDate && isoValue) {
            let toDateParsed = Date.parse(isoValue);
            let fromDateParsed = Date.parse(fromDate);
            if (toDateParsed <= fromDateParsed) {
                dispatch(setDisplayMessage('The "to" date must be after the "from" date.'));
                dispatch(setDisplayMessageType("error"))
                return;
            }
        }
        if (isoValue) {
            let toDateParsed = Date.parse(isoValue);
            if (toDateParsed > getMaxToDate().getTime()) {
                dispatch(setDisplayMessage('The "to" date cannot be later than one minute before the current time.'));
                dispatch(setDisplayMessageType("error"))
                return;
            }
        }

        setXApiQuery(translateQueryToXApi(userQuery, includeRetweets, fromDate, isoValue));
        setToDate(isoValue);
        dispatch(clearDisplayMessage());
    }

    const onSubmitEstimateClicked = () => {
        dispatch(clearDisplayMessage());
        if (!queryTitle || queryTitle.trim() === "")
        {
            dispatch(setDisplayMessage("Query title must be filled in"));
            dispatch(setDisplayMessageType("error"))
            return
        }

        // TODO: add time range warning for large queries
        dispatch(clearDisplayMessage());
        let translatedQuery: IXSifterQueryRoot | null = translateQueryToXApi(userQuery, includeRetweets, fromDate, toDate);
        setXApiQuery(translatedQuery);

        let validationError = isValidXQuery(translatedQuery);
        if (validationError) {
            dispatch(setDisplayMessage(validationError));
            dispatch(setDisplayMessageType("error"))
            return
        }

        setIsGettingEstimate(true);

        // save the query
        let queryToSave = getQueryForSave(translatedQuery);
        let saveResult: Promise<IXSifterCreateSavedQueryResponse> = (currentSavedQueryId && !props.duplicateQuery) ?
            queryService.updateSavedQuery(currentSavedQueryId, queryToSave, queryTitle, lastEstimateCount) :
            queryService.saveNewQuery(queryToSave, queryTitle, lastEstimateCount);

        saveResult.then((saveResponse) => {
            queryService.setQueryTraceId(saveResponse.currentQueryId);
            dispatch(setCurrentQueryId(queryService.getQueryTraceId()));
            setCurrentSavedQueryId(saveResponse.savedQueryId);

            // validate the saved query
            queryService.translateSavedQuery(saveResponse.savedQueryId).then(() => {
                queryService.getEstimate(translatedQuery)
                .then((response) => {
                    queryService.setQueryTraceId(response.currentQueryId);
                    dispatch(setCurrentQueryId(queryService.getQueryTraceId()));
                    setEstimateResponse (response);
                    setLastEstimateCount(response.count);
                    if (response.hasError) {
                        setEstimateTooLarge(true);
                        setEstimateErrorMessage(response.errorDescription ?? "");
                    } else if (response.count > response.creditBalance.availableBalance) {
                        setEstimateTooLarge(true);
                        setEstimateErrorMessage(`The estimated count of ${formatNumberString(response.count)} tweets is greater than your available balance of ${formatNumberString(response.creditBalance.availableBalance)} credits. `);
                    } else {
                        setEstimateTooLarge(false);
                        setEstimateErrorMessage("");
                    }
                    setOpenEstimateDialog(true);
                }).catch((error) => {
                    dispatch(setDisplayMessage(JSON.stringify(error)));
                    dispatch(setDisplayMessageType("error"))
                }).finally(() => {
                    setIsGettingEstimate(false);
                })
            })
            .catch((error) => {
                dispatch(setDisplayMessage("The query is not valid: " +error));
                dispatch(setDisplayMessageType("error"))
                setIsGettingEstimate(false);
            });
        })
        .catch((error: AxiosError) => {
            dispatch(setDisplayMessage(tryGetErrorMessage(error).error?.errorMessage ?? "An unknown error occurred."));
            dispatch(setDisplayMessageType("error"))
            setIsGettingEstimate(false);
        });
    }

    const onSubmitConfirmRequested = () => {
        setOpenEstimateDialog(false);
        setOpenSubmitConfirmDialog(true);
    }

    const onCancelSubmitConfirm = () => {
        setOpenSubmitConfirmDialog(false);
        setOpenEstimateDialog(true);
    }

    const onConfirmSubmit = () => {
        setOpenSubmitConfirmDialog(false);
        onSubmitClicked();
    }

    const onSubmitClicked = () => {
        setOpenEstimateDialog(false);
        dispatch(clearDisplayMessage());
        if (!queryTitle || queryTitle.trim() === "")
        {
            dispatch(setDisplayMessage("Query title must be filled in"));
            dispatch(setDisplayMessageType("error"))
            return
        }

        let translatedQuery: IXSifterQueryRoot | null = translateQueryToXApi(userQuery, includeRetweets,fromDate, toDate);
        setXApiQuery(translatedQuery);
        setIsGettingEstimate(true);

        // choose project + archive
        queryService.getProjectList()
            .then((response) => {
                let projects: IDiscoverTextProject[] = [];
                for (let i = 0; i < response.items.length; i++) {
                    projects.push(response.items[i]);
                }
                setProjectList(projects);
                setOpenProjectDialog(true);
            })
            .catch((error) => {
                setIsGettingEstimate(false);
                showApiError(dispatch, error, "Could not load projects");
            });
    }

    const onSelectedProject = (event: SelectChangeEvent<typeof selectedProjectId>) => {
        setSelectedProjectId(Number(event.target.value) || null);
    }

    const onCloseEstimateDialog = () => {
        setOpenEstimateDialog(false);
        setIsGettingEstimate(false);
    }

    const onCloseProjectDialog = () => {
        setOpenProjectDialog(false);
        setIsGettingEstimate(false);
        setOpenEstimateDialog(false);
    }

    const onProjectSelected = () => {
        if (!selectedProjectId) {
            alert("please select a project");
            return;
        }

        setArchiveDestination("new");
        setSelectedExistingArchiveId(null);
        setCreateUserDescArchive(false);
        setUserDescArchiveNameOverride(null);
        setUserDescDestination("new");
        setSelectedUserDescArchiveId(null);
        setArchiveList(null);
        setIsLoadingArchiveList(true);
        setOpenArchiveNameDialog(true);

        queryService.getDiscoverTextArchivesForProject(selectedProjectId)
            .then((response) => {
                setArchiveList(response.items);
            })
            .catch((error) => {
                dispatch(setDisplayMessage(JSON.stringify(error)));
                dispatch(setDisplayMessageType("error"))
            })
            .finally(() => {
                setIsLoadingArchiveList(false);
            });
    }

    const onCloseArchiveNameDialog = () => {
        setOpenArchiveNameDialog(false);
    }

    const onArchiveNameChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setNewArchiveName(event.target.value);
    }

    const onArchiveDestinationChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setArchiveDestination(event.target.value as "new" | "existing");
    }

    const onSelectedExistingArchive = (event: SelectChangeEvent<typeof selectedExistingArchiveId>) => {
        setSelectedExistingArchiveId(Number(event.target.value) || null);
    }

    const getDefaultUserDescArchiveName = (): string => {
        const baseName = archiveDestination === "existing" ?
            (archiveList?.find(x => x.id === selectedExistingArchiveId)?.name ?? "") :
            newArchiveName;
        return `${baseName}_user_description`;
    }

    const userDescArchiveName = userDescArchiveNameOverride ?? getDefaultUserDescArchiveName();

    const onUserDescArchiveNameChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setUserDescArchiveNameOverride(event.target.value);
    }

    const onUserDescDestinationChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setUserDescDestination(event.target.value as "new" | "existing");
    }

    const onSelectedUserDescArchive = (event: SelectChangeEvent<typeof selectedUserDescArchiveId>) => {
        setSelectedUserDescArchiveId(Number(event.target.value) || null);
    }

    const getDateFromString = (value: string | null | undefined): Dayjs | null => {
        if (!value)
            return null;
        return dayjs.utc(value)
    }

    const formatIsoDateUtc = (value: string | null | undefined): string => {
        if (!value)
            return "";
        return dayjs.utc(value).format("M/D/YYYY h:mm A") + " UTC";
    }

    // the "to" date must be at least 1 minute before "now" - if it's too close
    // (or in the future), back it off to exactly 1 minute before the current time
    const getSafeToDate = (value?: string | null): string | null | undefined => {
        if (!value)
            return value;
        const toDateMs = Date.parse(value);
        if (isNaN(toDateMs))
            return value;
        if (Date.now() - toDateMs < MIN_TO_DATE_BUFFER_MS) {
            return getMaxToDate().toISOString();
        }
        return value;
    }

    const getQueryForSave = (query: IXSifterQueryRoot | null | undefined): IXSifterQueryRoot | {} => {
        if (!query)
            return {};
        const safeToDate = getSafeToDate(query.toDate);
        if (safeToDate === query.toDate)
            return query;
        return { ...query, toDate: safeToDate };
    }

    const onDateRangeSelectChange = (event: SelectChangeEvent<number>) => {
        let now = getMaxToDate()
        setToDate(now.toISOString());
        now.setHours(now.getHours() - 24);
        setFromDate(now.toISOString());
        setShowCustomDateRange(false);
        setDateRangeSetting(3);

        switch (event.target.value) {
            case 0:
                setToDate(now.toISOString());
                now.setHours(now.getHours() - 1);
                setFromDate(now.toISOString());
                setShowCustomDateRange(false);
                setDateRangeSetting(0);
                break;
            case 1:
                setToDate(now.toISOString());
                now.setHours(now.getHours() - 9);
                setFromDate(now.toISOString());
                setShowCustomDateRange(false);
                setDateRangeSetting(1);
                break;
            case 2:
                setToDate(now.toISOString());
                now.setHours(now.getHours() - 12);
                setFromDate(now.toISOString());
                setShowCustomDateRange(false);
                setDateRangeSetting(2);
                break;
            case 3:
                setToDate(now.toISOString());
                now.setHours(now.getHours() - 24);
                setFromDate(now.toISOString());
                setShowCustomDateRange(false);
                setDateRangeSetting(3);
                break;
            case 4:
                setToDate(now.toISOString());
                now.setHours(now.getHours() - 48);
                setFromDate(now.toISOString());
                setShowCustomDateRange(false);
                setDateRangeSetting(4);
                break;
            case 5:
                setToDate(now.toISOString());
                now.setHours(now.getHours() - 168);
                setFromDate(now.toISOString());
                setShowCustomDateRange(false);
                setDateRangeSetting(5);
                break;
            case 6:
                setToDate(now.toISOString());
                now.setHours(now.getHours() - 336);
                setFromDate(now.toISOString());
                setShowCustomDateRange(false);
                setDateRangeSetting(6);
                break;
            case 7:
                setToDate(now.toISOString());
                now.setHours(now.getHours() - 672);
                setFromDate(now.toISOString());
                setShowCustomDateRange(false);
                setDateRangeSetting(7);
                break;
            default:
                setFromDate(fromDate);
                setToDate(toDate);
                setShowCustomDateRange(true);
                setDateRangeSetting(8);
                break;
        }
    }

    const onArchiveNameSaved = () => {
        if (archiveDestination === "new") {
            if (!newArchiveName || newArchiveName.trim() === "") {
                alert("Please enter an archive name");
                return;
            }
        } else {
            if (!selectedExistingArchiveId) {
                alert("Please select an existing archive");
                return;
            }
        }

        if (createUserDescArchive) {
            if (userDescDestination === "new") {
                if (!userDescArchiveName || userDescArchiveName.trim() === "") {
                    alert("Please enter a user description archive name");
                    return;
                }
            } else if (!selectedUserDescArchiveId) {
                alert("Please select an existing archive for the user descriptions");
                return;
            }
        }

        let projectId = selectedProjectId;
        setOpenArchiveNameDialog(false);
        setOpenProjectDialog(false);

        // save the query
        let queryToSave = getQueryForSave(xApiQuery);
        let result: Promise<IXSifterCreateSavedQueryResponse> = (currentSavedQueryId && !props.duplicateQuery) ?
            queryService.updateSavedQuery(currentSavedQueryId, queryToSave, queryTitle, lastEstimateCount) :
            queryService.saveNewQuery(queryToSave, queryTitle, lastEstimateCount);

        result.then((response) => {
                queryService.setQueryTraceId(response.currentQueryId);
                dispatch(setCurrentQueryId(queryService.getQueryTraceId()));
                setCurrentSavedQueryId(response.savedQueryId);
                // use the saved query ID to do the submit...
                let userDescOptions = createUserDescArchive ? {
                    createUserDescriptionArchive: true,
                    userDescriptionArchiveName: userDescDestination === "new" ? userDescArchiveName.trim() : undefined,
                    userDescriptionExistingArchiveId: userDescDestination === "existing" ? (selectedUserDescArchiveId || undefined) : undefined
                } : undefined;
                let submitPromise = archiveDestination === "existing" ?
                    queryService.submitFetch(response.savedQueryId, projectId || 0, undefined, selectedExistingArchiveId || undefined, userDescOptions) :
                    queryService.submitFetch(response.savedQueryId, projectId || 0, newArchiveName || "(no archive name)", undefined, userDescOptions);
                submitPromise
                    .then((response) => {
                        queryService.setQueryTraceId(response.currentQueryId);
                        dispatch(setCurrentQueryId(queryService.getQueryTraceId()));
                        dispatch(setDisplayMessage("Download task started successfully"));
                        dispatch(setDisplayMessageType("success"));
                        navigate("/app");
                    })
                    .catch((error) => {
                        dispatch(setDisplayMessage(JSON.stringify(error)));
                        dispatch(setDisplayMessageType("error"))
                    })
            })
            .catch((error) => {
                dispatch(setDisplayMessage(JSON.stringify(error)));
                dispatch(setDisplayMessageType("error"))
            })
    }

    const onReviseWithSamplingClicked = () => {
        let queryToSave = getQueryForSave(xApiQuery);
        let result: Promise<IXSifterCreateSavedQueryResponse> = (currentSavedQueryId && !props.duplicateQuery) ?
            queryService.updateSavedQuery(currentSavedQueryId, queryToSave, queryTitle, lastEstimateCount) :
            queryService.saveNewQuery(queryToSave, queryTitle, lastEstimateCount);

        result.then((response) => {
            queryService.setQueryTraceId(response.currentQueryId);
            dispatch(setCurrentQueryId(queryService.getQueryTraceId()));
            setCurrentSavedQueryId(response.savedQueryId);
            navigate(`/app/refinequery?queryId=${response.savedQueryId}`);
        })
        .catch((error: AxiosError) => {
            dispatch(setDisplayMessage(JSON.stringify(error.message)));
            dispatch(setDisplayMessageType("error"))
        });
    }

    // the choices are shown until the user picks one and clicks "Update request"
    const awaitingClarificationChoice = !!initialQueryNeedsClarification && clarificationChoices.length > 0;
    const isTitleValid = queryTitle.trim().length > 0;
    const hasFilledFilter = queryHasFilledFilter(userQuery);

    useEffect(() => {
        dispatch(setHasQueryTitle(isTitleValid));
    }, [isTitleValid, dispatch]);

    if (showChoice) {
        return (
            <ThemeProvider theme={xSifterQueryBuilderTheme}>
                <XSifterProcessInfoDialog open={openProcessInfoDialog} onClose={() => setOpenProcessInfoDialog(false)} />
                { initialQueryNeedsClarification && <QueryMessage
                    color="warning"
                    title="We need a bit of clarification"
                    message={initialQueryNeedsClarification}
                >
                    <ClarificationChoices choices={clarificationChoices} onUpdateRequest={onUpdateRequest} />
                </QueryMessage>}
                <Box sx={{ maxWidth: 800 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", ml: "128px", mb: 1 }}>
                        <Typography variant="h3" sx={{ mt: 4 }}>New Query</Typography>
                        <IconButton sx={{ mt: 4}} aria-label="How XSifter works" onClick={() => setOpenProcessInfoDialog(true)}>
                            <InfoOutlinedIcon />
                        </IconButton>
                    </Box>
                    <Divider sx={{ my: 3 }} />
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                        <Box sx={{ flexShrink: 0, width: 112, py: 0.5, textAlign: "center", borderRadius: 1, bgcolor: "success.main", color: "#fff", fontWeight: "bold", whiteSpace: "nowrap" }}>Option One</Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                You can experiment using filters, groups, and Boolean operations to generate a simple or complex query.
                                After each iteration, you can review samples to inform the next query, and ask XSifter to provide a range of better options.
                            </Typography>
                            <Button variant="outlined" onClick={() => { setUseBuilder(true); setShowChoice(false); }} >
                                Use Query Builder
                            </Button>
                        </Box>
                    </Box>
                    <Divider sx={{ my: 3 }}>OR</Divider>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                    <Box sx={{ flexShrink: 0, width: 112, py: 0.5, textAlign: "center", borderRadius: 1, bgcolor: "success.main", color: "#fff", fontWeight: "bold", whiteSpace: "nowrap" }}>Option Two</Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Describe in plain English the posts you want to find. Your description will be
                        translated into a Twitter query for you to review, so that you can fetch posts
                        specific to your needs. Include topics, keywords, accounts, languages, or anything
                        else that matters to you.
                    </Typography>
                    <Box sx={{ position: "relative" }}>
                        <TextField
                            multiline
                            minRows={4}
                            fullWidth
                            label="Describe your query"
                            placeholder="e.g. Posts about electric vehicle battery recalls in the United States"
                            value={queryDescription}
                            disabled={isSubmittingDescription}
                            slotProps={{ htmlInput: { maxLength: 1024 } }}
                            helperText={`${queryDescription.length} / 1024`}
                            onChange={(e) => setQueryDescription(e.target.value)}
                        />
                        {isSubmittingDescription && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 1.5,
                                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                                    borderRadius: 1,
                                    zIndex: 1
                                }}
                            >
                                <CircularProgress />
                                <Typography variant="body1">Translating and creating your query...</Typography>
                            </Box>
                        )}
                    </Box>
                    <Box sx={{ mt: 2 }}>
                        <Button
                            variant="contained"
                            disabled={!queryDescription.trim() || isSubmittingDescription || awaitingClarificationChoice}
                            onClick={() => onQueryDescriptionSubmit()}
                        >
                            Submit
                        </Button>
                    </Box>
                    </Box>
                    </Box>
                </Box>
            </ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={xSifterQueryBuilderTheme}>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <IconButton aria-label="How XSifter works" onClick={() => setOpenProcessInfoDialog(true)}>
                    <InfoOutlinedIcon />
                </IconButton>
            </Box>
            <XSifterProcessInfoDialog open={openProcessInfoDialog} onClose={() => setOpenProcessInfoDialog(false)} />
            { queryRationale && <QueryMessage color="success" title="About this query" message={queryRationale} subdued={queryRationaleEdited} /> }
            <Grid container spacing={2}>
                <Grid size={1} />
                <Grid size={11}>
                    Enter your query below starting with a title, and then the query clauses.
                    When ready, submit the estimate. 
                    Once you retrieve the estimate, you can sample and refine your query, or you can submit the query to be fetched into a DiscoverText archive.
                </Grid>
                <Grid size={1}>
                    <LooksOneIcon fontSize="large" color='success' />
                </Grid>
                <Grid size={11}>
                    <TextField 
                        id="txtQueryTitle" 
                        fullWidth={true}
                        placeholder="New Query" 
                        label="Query Title" 
                        variant="standard" 
                        value={queryTitle}
                        disabled={!props.allowEdits} 
                        onChange={onQueryTitleChanged}
                        required
                    />
                </Grid>
                <Grid size={1}>
                    <LooksTwoIcon fontSize="large" color={isTitleValid ? 'success' : 'disabled'} />
                </Grid>
                <Grid size={11}>
                    <div id="ctlQueryBuilder">
                        {queryInitialized && (
                            <QueryBuilderComponent
                                existingQuery={userQuery}
                                onQueryChanged={handleQueryChange}
                                disabled={!isTitleValid}
                            />
                        )}
                    </div>
                </Grid>
                <Grid size={1} />
                <Grid size={1}>
                    Timespan: 
                </Grid>
                <Grid size={10}>
                    <Select id="id-date-preset-select"
                        defaultValue={3}
                        value={dateRangeSetting}
                        className="queryBuilderDateRange"
                        onChange={onDateRangeSelectChange}
                    >
                        <MenuItem value={0}>last hour</MenuItem>
                        <MenuItem value={1}>last 8 hours</MenuItem>
                        <MenuItem value={2}>last 12 hours</MenuItem>
                        <MenuItem value={3}>last 24 hours</MenuItem>
                        <MenuItem value={4}>last 48 hours</MenuItem>
                        <MenuItem value={5}>last 7 days</MenuItem>
                        <MenuItem value={6}>last 14 days</MenuItem>
                        <MenuItem value={7}>last 28 days</MenuItem>
                        <MenuItem value={8}>custom date range</MenuItem>
                    </Select>
                    <br />
                    {dateRangeSetting !== 8 && <>(From {formatIsoDateUtc(fromDate)} to {formatIsoDateUtc(toDate)})</> }
                </Grid>
                {showCustomDateRange && (<>
                <Grid size={1} />
                <Grid size={1}>
                    From date:
                </Grid>
                <Grid size={4}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                            className="queryBuilderDatePicker"
                            timezone="UTC"
                            onChange={handleFromDateChange}
                            closeOnSelect={true}
                            slotProps={{ field: {clearable: true}}}
                            value={getDateFromString(fromDate)}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid size={1}>
                    To date:
                </Grid>
                <Grid size={4}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                            className="queryBuilderDatePicker"
                            timezone="UTC"
                            onChange={handleToDateChange}
                            maxDateTime={dayjs.utc(getMaxToDate())}
                            closeOnSelect={true}
                            slotProps={{ field: {clearable: true}}}
                            value={getDateFromString(toDate)}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid size={1} />
                <Grid size={1} />
                <Grid size={1} />
                <Grid size={10}>
                    <Typography variant="caption" color="text.secondary">
                        All times are in UTC.
                    </Typography>
                </Grid>
                </>)}
                <Grid size={1} />
                <Grid size={11}>
                    <FormGroup>
                        <FormControlLabel control={<Switch value={includeRetweets} onChange={onIncludeRetweetsChange} />} label="Include retweets in data" />
                    </FormGroup>
                </Grid>
                <Grid size={1} />
                <Grid size={11}>
                    {estimateResponse && (
                        <div>
                            Current query estimated posts: {formatNumberString(estimateResponse.count)}
                        </div>
                    )}
                    <div>
                        Credit balance: {formatNumberString(props.currentUser?.creditBalance.ledgerBalance ?? 0)} (available balance: {formatNumberString(props.currentUser?.creditBalance.availableBalance ?? 0)})
                    </div>
                </Grid>
                <Grid size={1}>
                    <Looks3Icon fontSize="large" color={isTitleValid && hasFilledFilter ? 'success' : 'disabled'} />
                </Grid>
                <Grid size={11} sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Button variant="contained" color="secondary" disabled={isGettingEstimate || !isTitleValid || !hasFilledFilter} onClick={onSubmitEstimateClicked}>Get Estimate</Button>
                    <Button variant="contained" color="secondary" disabled={!isTitleValid} onClick={() => navigate("/app")}>Cancel</Button>
                </Grid>
            </Grid>
            {false && <><Accordion>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="div-translated-query-content"
                    id="div-translated-query-header"
                >
                    <Typography component="span">(debug) Translated Query:</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <pre>
                    {JSON.stringify(xApiQuery, null, 2)}
                    </pre>
                </AccordionDetails>
            </Accordion></>}
            {false && <><Accordion>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="div-raw-query-content"
                    id="div-raw-query-header"
                >
                    <Typography component="span">(debug) Raw Query:</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <pre>
                    {JSON.stringify(userQuery, null, 2)}
                    </pre>
                </AccordionDetails>
            </Accordion></>}
            <Dialog open={openEstimateDialog} onClose={onCloseEstimateDialog}>
                <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    Estimated Posts
                    <IconButton aria-label="More information about these options" onClick={() => setOpenEstimateInfoDialog(true)}>
                        <InfoOutlinedIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {estimateResponse && (
                        <>
                    {estimateResponse.count <= 0 ? (
                        <p>
                            No posts could be found for your query. Please go back and edit your query.
                        </p>
                    ) : (<>
                    <p>
                        Estimated posts: {Intl.NumberFormat("en-US").format(estimateResponse.count)}
                    </p>
                    {estimateTooLarge ? (
                        <p>
                            {estimateErrorMessage}

                            Please refine your query or add more credits, or use the sample and refinement process
                        </p>
                    ) : (
                        <p>
                            You can sample and refine your query, or you can submit it and fetch these items.
                            You can also go back and edit your query manually if you want.
                        </p>
                    )}
                    </>)}
                    </>)}
                </DialogContent>
                <DialogActions sx={{ flexDirection: "column", alignItems: "center", gap: 1, paddingBottom: 4, "& > :not(style) ~ :not(style)": { marginLeft: 0 }, "& .MuiButton-root": { width: "90%" } }}>
                    {(estimateResponse?.count ?? 0) > 0 &&
                        <Button variant="contained" sx={{ backgroundColor: "#43a047", color: "#fff", "&:hover": { backgroundColor: "#388e3c" } }} onClick={onReviseWithSamplingClicked}>Sample and Refine</Button>}
                    <Button variant="contained" sx={{ backgroundColor: "#fdd835", color: "#000", "&:hover": { backgroundColor: "#fbc02d" } }} onClick={onCloseEstimateDialog}>Back to Editing</Button>
                    {(estimateResponse?.count ?? 0) > 0 &&
                        <Button variant="contained" sx={{ backgroundColor: "#ef9a9a", color: "#000", "&:hover": { backgroundColor: "#e57373" } }} disabled={estimateTooLarge} onClick={onSubmitConfirmRequested}>Submit this Query</Button>}
                </DialogActions>
            </Dialog>
            <Dialog open={openSubmitConfirmDialog} onClose={onCancelSubmitConfirm}>
                <DialogTitle>Are you finished refining your query?</DialogTitle>
                <DialogContent dividers>
                    <Typography gutterBottom>
                        Submitting will fetch all posts matching this query. Make sure you are done sampling and refining before you continue.
                    </Typography>
                    <Typography gutterBottom>Next steps:</Typography>
                    <Box component="ol" sx={{ marginTop: 0, paddingLeft: "24px" }}>
                        <li>Choose the DiscoverText project to store the results in.</li>
                        <li>Choose (or create) the archive within that project that will hold the posts.</li>
                    </Box>
                    <Typography gutterBottom>
                        Once the data transfer starts, XSifter will reserve the estimated amount of credits
                        ({estimateResponse ? formatNumberString(estimateResponse.count) : 0} credits).
                        You will only be charged credits for the posts that are successfully transferred into your DiscoverText account.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onCancelSubmitConfirm}>Not Yet</Button>
                    <Button variant="contained" onClick={onConfirmSubmit}>Yes, Continue</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={openEstimateInfoDialog} onClose={() => setOpenEstimateInfoDialog(false)}>
                <DialogTitle>What Would You Like to Do Next?</DialogTitle>
                <DialogContent dividers>
                    <Typography gutterBottom>
                        The estimate shows roughly how many posts match your query, so you can decide what to do before any posts are fetched.
                    </Typography>
                    <Typography gutterBottom>
                        <strong>Sample and Refine:</strong> Helps you improve your query by checking it against real posts before you fetch everything.
                    </Typography>
                    <Box component="ol" sx={{ marginTop: 0, paddingLeft: "24px" }}>
                        <li>Retrieve a small sample (about 10 posts) that match your current query.</li>
                        <li>Score each sampled post as relevant or not relevant.</li>
                        <li>Click Refine Query. You can optionally add notes describing the kinds of posts you want to see more or less of.</li>
                        <li>XSifter uses your scores and notes to suggest improved versions of your query. Choose one, get a new estimate, and repeat the process as many times as you like.</li>
                    </Box>
                    <Typography gutterBottom>
                        <strong>Back to Editing:</strong> Close this dialog and return to the query builder to change your query manually. You will need to get a new estimate after making changes.
                    </Typography>
                    <Typography gutterBottom>
                        <strong>Submit this Query:</strong> Fetch all matching posts into a DiscoverText archive. This uses credits based on the number of posts, and is unavailable if the estimate is larger than your available balance.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenEstimateInfoDialog(false)}>Close</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={openProjectDialog} onClose={onCloseProjectDialog}>
                <DialogTitle>
                    Select project for data storage
                </DialogTitle>
                <DialogContent dividers>
                    <FormControl>
                        <Typography gutterBottom>An estimated {estimateResponse && (Intl.NumberFormat("en-US").format(estimateResponse.count))} tweets will be fetched.</Typography>
                        <Typography gutterBottom>Select a DiscoverText project to download the data into.</Typography>
                        <div>
                            <Select labelId="lblProjectSelect"
                                id="drpProjectSelection"
                                value={selectedProjectId || 0}
                                onChange={onSelectedProject}
                                renderValue={(selected) => {
                                    if (selected === 0) {
                                        return <em>-- select a project --</em>
                                    }
                                    var filteredList = projectList?.filter(x => x.id === selected);
                                    if (!filteredList || filteredList?.length === 0) {
                                        return <em>-- no projects --</em>
                                    }
                                    return filteredList[0].name;
                                }}
                            >
                                <MenuItem disabled value={0}>
                                    <em>-- select a project --</em>
                                </MenuItem>
                                {projectList && projectList.map(x => <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>)}
                            </Select>
                        </div>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onCloseProjectDialog}>Cancel</Button>
                    <Button onClick={onProjectSelected}>Ok</Button>
                </DialogActions>            
            </Dialog>
            <Dialog open={openArchiveNameDialog} onClose={onCloseArchiveNameDialog}>
                <DialogTitle>
                    Archive Destination
                </DialogTitle>
                <DialogContent dividers>
                    <FormControl fullWidth>
                        <Typography gutterBottom>Choose where to fetch this data into.</Typography>
                        <RadioGroup value={archiveDestination} onChange={onArchiveDestinationChanged}>
                            <FormControlLabel value="new" control={<Radio />} label="Create a new archive" />
                            {archiveDestination === "new" && (
                                <div>
                                    <TextField
                                        id="txtNewArchiveName"
                                        fullWidth={true}
                                        placeholder="Archive Name"
                                        label="New Archive Name"
                                        variant="filled"
                                        disabled={!props.allowEdits}
                                        value={newArchiveName}
                                        onChange={onArchiveNameChanged}
                                    />
                                </div>
                            )}
                            <FormControlLabel
                                value="existing"
                                control={<Radio />}
                                label="Add to an existing archive"
                                disabled={!isLoadingArchiveList && (!archiveList || archiveList.length === 0)}
                            />
                            {isLoadingArchiveList ? (
                                <div>
                                    <Typography variant="body2">Loading archives...</Typography>
                                </div>
                            ) : (!archiveList || archiveList.length === 0) ? (
                                <div>
                                    <Typography variant="body2">No existing archives found for this project.</Typography>
                                </div>
                            ) : archiveDestination === "existing" && (
                                <div>
                                    <Select labelId="lblExistingArchiveSelect"
                                        id="drpExistingArchiveSelection"
                                        fullWidth
                                        value={selectedExistingArchiveId || 0}
                                        onChange={onSelectedExistingArchive}
                                        renderValue={(selected) => {
                                            if (!selected) {
                                                return <em>-- select an archive --</em>
                                            }
                                            var filteredList = archiveList?.filter(x => x.id === selected);
                                            if (!filteredList || filteredList?.length === 0) {
                                                return <em>-- no archives --</em>
                                            }
                                            return filteredList[0].name;
                                        }}
                                    >
                                        <MenuItem disabled value={0}>
                                            <em>-- select an archive --</em>
                                        </MenuItem>
                                        {archiveList.map(x => <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>)}
                                    </Select>
                                </div>
                            )}
                        </RadioGroup>
                    </FormControl>
                    <Divider sx={{ my: 2 }} />
                    <FormControl fullWidth>
                        <FormControlLabel
                            control={<Switch checked={createUserDescArchive} onChange={(e) => setCreateUserDescArchive(e.target.checked)} />}
                            label="Also create User Description Archive"
                        />
                        {createUserDescArchive && (
                            <RadioGroup value={userDescDestination} onChange={onUserDescDestinationChanged}>
                                <FormControlLabel value="new" control={<Radio />} label="Create a new user description archive" />
                                {userDescDestination === "new" && (
                                    <div>
                                        <TextField
                                            id="txtNewUserDescArchiveName"
                                            fullWidth={true}
                                            placeholder="Archive Name"
                                            label="New User Description Archive Name"
                                            variant="filled"
                                            disabled={!props.allowEdits}
                                            value={userDescArchiveName}
                                            onChange={onUserDescArchiveNameChanged}
                                        />
                                    </div>
                                )}
                                <FormControlLabel
                                    value="existing"
                                    control={<Radio />}
                                    label="Add to an existing archive"
                                    disabled={!isLoadingArchiveList && (!archiveList || archiveList.length === 0)}
                                />
                                {isLoadingArchiveList ? (
                                    <div>
                                        <Typography variant="body2">Loading archives...</Typography>
                                    </div>
                                ) : (!archiveList || archiveList.length === 0) ? (
                                    <div>
                                        <Typography variant="body2">No existing archives found for this project.</Typography>
                                    </div>
                                ) : userDescDestination === "existing" && (
                                    <div>
                                        <Select
                                            id="drpExistingUserDescArchiveSelection"
                                            fullWidth
                                            value={selectedUserDescArchiveId || 0}
                                            onChange={onSelectedUserDescArchive}
                                            renderValue={(selected) => {
                                                if (!selected) {
                                                    return <em>-- select an archive --</em>
                                                }
                                                var filteredList = archiveList?.filter(x => x.id === selected);
                                                if (!filteredList || filteredList?.length === 0) {
                                                    return <em>-- no archives --</em>
                                                }
                                                return filteredList[0].name;
                                            }}
                                        >
                                            <MenuItem disabled value={0}>
                                                <em>-- select an archive --</em>
                                            </MenuItem>
                                            {archiveList.map(x => <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>)}
                                        </Select>
                                    </div>
                                )}
                            </RadioGroup>
                        )}
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onCloseArchiveNameDialog}>Cancel</Button>
                    <Button onClick={onArchiveNameSaved}>Ok</Button>
                </DialogActions>
            </Dialog>

        </ThemeProvider>
    );
}

export default XSifterQueryBuilder;